import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { getIdTokenResult } from "firebase/auth";
import { auth, functions, selectedAdminEnvironment } from "../config/firebase";
import { refundObservationRequest, validateRefundObservation, validateRefundSubmission,
	refundResultComplete, recordedRefundMessage } from "./refundObservation";
import OrderSupportTimeline from "./OrderSupportTimeline";
import "./styles/OrderSupportDetail.css";

const formatMoney = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

const formatDate = (value) => {
	if (!value) return "--";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const OrderSupportDetail = () => {
	const { id } = useParams();
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [evidenceScope, setEvidenceScope] = useState(null);
	const [note, setNote] = useState("");
	const [noteStatus, setNoteStatus] = useState("needs_review");
	const [refundAmount, setRefundAmount] = useState("");
	const [refundReason, setRefundReason] = useState("");
	const [selectedPaymentId, setSelectedPaymentId] = useState("");
	const [pendingRefund, setPendingRefund] = useState(null);
	const [refundStorageError, setRefundStorageError] = useState("");
	const refundBusy = useRef(null);
	const refundScope = useRef(null);
	const verifiedRefundDetail = useRef(null);
	const printBusy = useRef(false);
	const mounted = useRef(true);
	const printScope = useRef(null);
	const detailRequest = useRef(0);
	const actorUid = auth.currentUser?.uid;
	const firebaseProject = selectedAdminEnvironment.projectId;
	const scopeKey = `${firebaseProject}:${actorUid}:${id}`;
	if (refundScope.current?.key !== scopeKey) refundScope.current = { key: scopeKey };
	const activeRefundScope = refundScope.current;
	printScope.current = scopeKey;
	const currentScope = () => mounted.current && refundScope.current === activeRefundScope &&
		selectedAdminEnvironment.projectId === firebaseProject && auth.currentUser?.uid === actorUid;
	useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
	const refundStorageKey = `scerv-refund-v1:${selectedAdminEnvironment.projectId}:${actorUid}:${id}`;
	const printRefundReceipt = async (ledger) => {
		if (printBusy.current) return;
		const user = auth.currentUser;
		const scope = printScope.current;
		const current = () => mounted.current && printScope.current === scope && auth.currentUser?.uid === user?.uid;
		let popup;
		printBusy.current = true;
		setSaving(true); setError(""); setMessage("");
		try {
			if (!user || user.uid !== actorUid || !order.restaurantId || order.id !== id) throw new Error("Reload the order and sign in before printing.");
			popup = window.open("", "_blank", "width=480,height=720");
			if (!popup) throw new Error("Allow pop-ups to print the refund receipt.");
			popup.opener = null;
			const token = await getIdTokenResult(user, true);
			if (!current()) throw new Error("Print scope changed. Reload before printing.");
			if (!["admin", "godmode", "scerv_admin", "super_admin"].includes(String(token.claims.role || "").toLowerCase())) {
				throw new Error("Scerv admin access is required.");
			}
			const key = `scerv-refund-print-v1:${scope}:${ledger.id}`;
			const saved = window.localStorage.getItem(key);
			const request = saved ? JSON.parse(saved) : {
				firebaseProject: selectedAdminEnvironment.projectId, restaurantId: order.restaurantId,
				orderId: id, sourceId: ledger.id, requestId: window.crypto.randomUUID(),
				intent: window.localStorage.getItem(`${key}:prepared`) ? "reprint" : "print",
			};
			if (request.orderId !== id || request.restaurantId !== order.restaurantId || request.sourceId !== ledger.id ||
				request.firebaseProject !== selectedAdminEnvironment.projectId || !request.requestId || !["print", "reprint"].includes(request.intent)) {
				throw new Error("Saved print request requires review.");
			}
			window.localStorage.setItem(key, JSON.stringify(request));
			const response = await httpsCallable(functions, "prepareScervAdminRefundReceiptPrint")(request);
			if (!current() || popup.closed) throw new Error("Print window or authorization scope changed.");
			const receipt = response.data;
			if (!receipt || Object.keys(request).some(field => receipt[field] !== request[field]) ||
				receipt.status !== "prepared" || receipt.deliveryStatus !== "not_confirmed" || !receipt.auditId ||
				typeof receipt.printableText !== "string" || !receipt.printableText || receipt.printableText.length > 20000) {
				throw new Error("Audited receipt response requires review.");
			}
			popup.document.title = "Scerv refund receipt";
			const sheet = popup.document.createElement("main");
			sheet.style.cssText = "font:14px monospace;max-width:360px;margin:24px auto;color:#111;white-space:pre-wrap;overflow-wrap:anywhere";
			sheet.textContent = receipt.printableText;
			popup.document.body.appendChild(sheet);
			window.localStorage.setItem(`${key}:prepared`, receipt.auditId);
			window.localStorage.removeItem(key);
			popup.focus();
			popup.print();
			setMessage(`Refund receipt prepared. Audit: ${receipt.auditId}. Physical printing is not confirmed.`);
		} catch (err) {
			if (popup && !popup.closed) popup.close();
			if (current()) setError(err.message || "Could not prepare the refund receipt. Retry uses the same saved request.");
		} finally {
			printBusy.current = false;
			if (mounted.current) setSaving(false);
		}
	};

	useEffect(() => {
		setPendingRefund(null);
		setRefundStorageError("");
		setSelectedPaymentId("");
		setRefundAmount("");
		setRefundReason("");
		setMessage("");
		setSaving(false);
		if (!actorUid) return;
		try {
			const saved = window.localStorage.getItem(refundStorageKey);
			if (saved !== null) {
				const command = JSON.parse(saved);
				if (!command || typeof command !== "object" || Array.isArray(command) || command.orderId !== id ||
					typeof command.requestId !== "string" || !command.requestId.trim()) throw new Error("Invalid saved refund request");
				setPendingRefund(command);
			}
		} catch {
			setRefundStorageError("Saved refund request could not be read. Contact support before issuing another refund.");
		}
	}, [actorUid, id, refundStorageKey]);

	const applyRefundResult = async (result, saved) => {
		if (window.localStorage.getItem(refundStorageKey) !== saved) throw new Error("Saved refund request changed. Reload before continuing.");
		const complete = refundResultComplete(result);
		if (complete) {
			window.localStorage.removeItem(refundStorageKey);
			setPendingRefund(null);
			setRefundAmount("");
			setRefundReason("");
		}
		setMessage(recordedRefundMessage(result));
		if (complete) await loadDetail();
	};

	const sendRefundCommand = async (command) => {
		const verified = verifiedRefundDetail.current;
		if (!currentScope() || !actorUid || verified?.scope !== activeRefundScope) throw new Error("Refund scope changed. Reload the original order.");
		refundObservationRequest(command, { firebaseProject, restaurantId: verified.restaurantId, orderId: id });
		const saved = window.localStorage.getItem(refundStorageKey);
		if (saved !== JSON.stringify(command)) throw new Error("Saved refund request changed. Reload before continuing.");
		const response = await httpsCallable(functions, "refundScervStripeOrder")(command);
		if (!currentScope() || verifiedRefundDetail.current !== verified) return;
		await applyRefundResult(validateRefundSubmission(response.data), saved);
	};

	const checkRefundStatus = async () => {
		if (!pendingRefund || !currentScope() || refundBusy.current?.scope === activeRefundScope) return;
		const operation = { scope: activeRefundScope };
		refundBusy.current = operation;
		setSaving(true);
		setError("");
		setMessage("");
		try {
			const verified = verifiedRefundDetail.current;
			if (!currentScope() || !actorUid || verified?.scope !== activeRefundScope) throw new Error("Reload the original order before checking its refund.");
			const request = refundObservationRequest(pendingRefund, { firebaseProject, restaurantId: verified.restaurantId, orderId: id });
			const stored = window.localStorage.getItem(refundStorageKey);
			if (!stored || JSON.stringify(JSON.parse(stored)) !== JSON.stringify(pendingRefund)) throw new Error("Saved refund request changed. Reload before continuing.");
			// Bind older saved commands to verified detail without changing their request/payment identity.
			const scopedPending = { ...pendingRefund, ...request };
			const saved = JSON.stringify(scopedPending);
			window.localStorage.setItem(refundStorageKey, saved);
			setPendingRefund(scopedPending);
			const response = await httpsCallable(functions, "getScervRefundStatus")(request);
			if (!currentScope() || verifiedRefundDetail.current !== verified) return;
			const result = validateRefundObservation(response.data, request, scopedPending);
			await applyRefundResult(result, saved);
		} catch (err) {
			if (currentScope()) setError(`${err.message || "Could not check recorded refund status."} Saved request retained; do not submit another refund.`);
		}
		finally {
			if (refundBusy.current === operation) {
				refundBusy.current = null;
				if (currentScope()) setSaving(false);
			}
		}
	};

	const loadDetail = useCallback(async () => {
		const request = ++detailRequest.current;
		const current = () => mounted.current && refundScope.current === activeRefundScope &&
			selectedAdminEnvironment.projectId === firebaseProject && auth.currentUser?.uid === actorUid && detailRequest.current === request;
		verifiedRefundDetail.current = null;
		setLoading(true);
		setDetail(null);
		setError("");
		try {
			if (!actorUid) throw new Error("Sign in to view this order.");
			const getDetail = httpsCallable(functions, "getScervOrderSupportDetail");
			const response = await getDetail({ orderId: id });
			if (!current()) return;
			if (response.data?.order?.id !== id) throw new Error("Order response identity changed.");
			verifiedRefundDetail.current = { scope: activeRefundScope, restaurantId: response.data.order.restaurantId };
			setDetail(response.data);
		} catch (err) {
			console.error("Failed to load order support detail:", err);
			if (current()) setError("Failed to load order detail.");
		} finally {
			if (current()) setLoading(false);
		}
	}, [id, actorUid, firebaseProject, activeRefundScope]);

	useEffect(() => {
		loadDetail();
	}, [loadDetail]);

	const order = useMemo(() => detail?.order || {}, [detail]);
	const isOpenCheck = order.settlementState === "open";
	const remainingUnpaidCents = Number.isSafeInteger(order.remainingUnpaidCents) && order.remainingUnpaidCents >= 0
		? order.remainingUnpaidCents : null;
	const refundSummary = useMemo(
		() => order.refundSummary || {},
		[order],
	);
	const aggregateRefundableCents = Number(refundSummary.refundableCents || 0);
	const refundPayments = Array.isArray(order.refundPayments) ? order.refundPayments : [];
	const selectedPayment = selectedPaymentId ? refundPayments.find(row => row.paymentId === selectedPaymentId)
		: refundPayments.length === 1 ? refundPayments[0] : null;
	const validPaymentBalance = selectedPayment &&
		["capturedCents", "refundedCents", "pendingRefundCents", "refundableCents"].every(field =>
			Number.isSafeInteger(selectedPayment[field]) && selectedPayment[field] >= 0) &&
		selectedPayment.refundedCents + selectedPayment.pendingRefundCents + selectedPayment.refundableCents === selectedPayment.capturedCents;
	const refundableCents = validPaymentBalance ? selectedPayment.refundableCents : 0;
	const canRefundPayment = currentScope() && !refundStorageError && verifiedRefundDetail.current?.scope === activeRefundScope &&
		typeof order.restaurantId === "string" && !!order.restaurantId.trim() && order.id === id && validPaymentBalance && selectedPayment.canRefund === true &&
		/^pi_[A-Za-z0-9]+$/.test(selectedPayment.paymentIntentId || "") && refundableCents > 0 &&
		!order.raw?.externalPaymentReviewRequired && !order.raw?.refundEffectsPending;
	const paymentIntentId = order.paymentIntentId || "";

	const paymentRows = useMemo(
		() => [
			["Subtotal", order.subtotal],
			["Discounts", order.discountTotal],
			["Tax", order.taxAmount],
			["Gratuity", order.gratuityAmount],
			["Platform fee", order.platformFee],
			["Processor fee", order.processorFee],
			["Restaurant transfer", order.restaurantTransferAmount],
			[isOpenCheck ? "Captured so far" : "Total paid", order.totalPrice],
			["Refunded", refundSummary.totalRefundedCents],
			["Pending refunds", refundSummary.pendingRefundCents],
			["Refundable", aggregateRefundableCents],
		],
		[order, refundSummary, aggregateRefundableCents, isOpenCheck],
	);

	const addNote = async (event) => {
		event.preventDefault();
		setMessage("");
		setError("");
		if (!note.trim()) {
			setError("Enter a support note.");
			return;
		}

		setSaving(true);
		try {
			const addSupportNote = httpsCallable(functions, "addScervOrderSupportNote");
			await addSupportNote({ orderId: id, note, status: noteStatus });
			setNote("");
			setMessage("Support note saved.");
			await loadDetail();
		} catch (err) {
			console.error("Failed to add support note:", err);
			setError("Failed to save support note.");
		} finally {
			setSaving(false);
		}
	};

	const submitRefund = async (event) => {
		event.preventDefault();
		if (refundBusy.current?.scope === activeRefundScope || pendingRefund || refundStorageError) return;
		setMessage("");
		setError("");

		if (!canRefundPayment) {
			setError("Select an eligible original card payment before requesting a refund.");
			return;
		}
		if (!/^\d+(\.\d{1,2})?$/.test(refundAmount)) {
			setError("Enter a refund amount with no more than two decimal places.");
			return;
		}
		const amountCents = Math.round(Number(refundAmount || 0) * 100);
		if (!Number.isSafeInteger(amountCents) || amountCents <= 0 || amountCents > refundableCents) {
			setError("Enter a valid refund amount within the refundable balance.");
			return;
		}
		if (!refundReason.trim()) {
			setError("Enter a refund reason.");
			return;
		}
		if (
			!window.confirm(
				`Refund ${formatMoney(amountCents)} for order ${
					order.readableOrderId || id
				} against payment ${selectedPayment.paymentIntentId}?`,
			)
		) {
			return;
		}

		setSaving(true);
		const operation = { scope: activeRefundScope };
		refundBusy.current = operation;
		try {
			if (!actorUid || !currentScope() || order.id !== id) throw new Error("Reload the order and sign in before requesting a refund.");
			if (window.localStorage.getItem(refundStorageKey) !== null) throw new Error("A saved refund request exists. Reload before continuing.");
			const command = {
				firebaseProject,
				restaurantId: order.restaurantId,
				requestId: window.crypto.randomUUID(),
				orderId: id,
				...(selectedPayment.paymentId ? { paymentId: selectedPayment.paymentId } : {}),
				amountCents,
				reason: refundReason.trim(),
				refundType: amountCents === refundableCents ? "full" : "partial",
			};
			window.localStorage.setItem(refundStorageKey, JSON.stringify(command));
			setPendingRefund(command);
			await sendRefundCommand(command);
		} catch (err) {
			console.error("Refund failed:", err);
			if (currentScope()) setError(`${err.message || "Refund outcome is unconfirmed."} Do not submit another refund while the saved request is unresolved.`);
		} finally {
			if (refundBusy.current === operation) {
				refundBusy.current = null;
				if (currentScope()) setSaving(false);
			}
		}
	};

	if (loading) {
		return <div className="order-support-container">Loading...</div>;
	}

	if (error && !detail) {
		return <div className="order-support-container error">{error}</div>;
	}

	return (
		<div className="order-support-container">
			<div className="order-support-header">
				<div>
					<Link to="/command-center">Back to Command Center</Link>
					<h1>{isOpenCheck ? "Open check" : "Order"} {order.readableOrderId || order.id || id}</h1>
					<p>
						{order.restaurantName || "Unknown restaurant"} ·{" "}
						{order.paymentStatus || "unknown"} ·{" "}
						{formatDate(order.fulfilledAt || order.openedAt)}
					</p>
					{isOpenCheck && <p role="status">Check remains open · Unpaid balance at last collection: {remainingUnpaidCents === null ? "Unavailable" : formatMoney(remainingUnpaidCents)}</p>}
				</div>
				<div className="order-support-total">
					<span>{isOpenCheck ? "Captured so far" : "Total paid"}</span>
					<strong>{formatMoney(order.totalPrice)}</strong>
				</div>
			</div>

			{message && <p className="order-support-message">{message}</p>}
			{error && <p className="order-support-error">{error}</p>}
			{order.id === id && currentScope() && /^[A-Za-z0-9_-]{1,128}$/.test(order.restaurantId || "") &&
				<details key={scopeKey} className="order-support-evidence" onToggle={event => setEvidenceScope(event.currentTarget.open ? scopeKey : null)}>
					<summary>Order evidence</summary>
					{evidenceScope === scopeKey && <OrderSupportTimeline key={scopeKey} restaurantId={order.restaurantId}
						orderId={id} projectId={firebaseProject} />}
				</details>}

			<div className="order-support-grid">
				<section className="order-support-panel">
					<h2>Customer</h2>
					<p>
						<strong>{order.customerName || "Scerv Guest"}</strong>
					</p>
					<p>{order.customerEmail || "--"}</p>
					{order.customerId && (
						<Link to={`/customers/${order.customerId}`}>Open Customer 360</Link>
					)}
				</section>

				<section className="order-support-panel">
					<h2>Restaurant</h2>
					<p>
						<strong>{order.restaurantName || "--"}</strong>
					</p>
					<p>{detail?.restaurant?.city || "--"}, {detail?.restaurant?.state || "--"}</p>
					{order.restaurantId && (
						<Link to={`/restaurants/${order.restaurantId}`}>
							Open Restaurant 360
						</Link>
					)}
				</section>

				<section className="order-support-panel">
					<h2>Payment Trace</h2>
					<dl>
						<dt>Processor</dt>
						<dd>{order.paymentProcessor || "--"}</dd>
						<dt>Payment intent</dt>
						<dd>{paymentIntentId || "--"}</dd>
						<dt>Connect type</dt>
						<dd>{order.stripeConnectChargeType || "--"}</dd>
						<dt>Transfer</dt>
						<dd>{order.stripeDestinationTransferId || "--"}</dd>
					</dl>
				</section>

				<section className="order-support-panel">
					<h2>Support Status</h2>
					<dl>
						<dt>Support status</dt>
						<dd>{order.raw?.supportStatus || "--"}</dd>
						<dt>Order status</dt>
						<dd>{order.orderStatus || "--"}</dd>
						<dt>Mode</dt>
						<dd>{order.orderMode || order.fulfillmentType || "--"}</dd>
						<dt>Table</dt>
						<dd>{order.table?.name || order.table?.tableNumber || "--"}</dd>
					</dl>
				</section>
			</div>

			<section className="order-support-panel">
				<h2>{isOpenCheck ? "Captured Payment Breakdown" : "Payment Breakdown"}</h2>
				<div className="order-money-grid">
					{paymentRows.map(([label, value]) => (
						<div key={label}>
							<span>{label}</span>
							<strong>{formatMoney(value)}</strong>
						</div>
					))}
				</div>
			</section>

			<section className="order-support-panel">
				<h2>{isOpenCheck ? "Captured Items" : "Items"}</h2>
				{order.items?.length ? (
					<table className="order-support-table">
						<thead>
							<tr>
								<th>Item</th>
								<th>Category</th>
								<th>Qty</th>
								<th>Price</th>
							</tr>
						</thead>
						<tbody>
							{order.items.map((item, index) => (
								<tr key={`${item.id || item.name}-${index}`}>
									<td>{item.name || item.dishName || "Item"}</td>
									<td>{item.category || "--"}</td>
									<td>{item.quantity || 1}</td>
									<td>{formatMoney(Math.round(Number(item.price || 0) * 100))}</td>
								</tr>
							))}
						</tbody>
					</table>
				) : (
					<p>No item details found.</p>
				)}
			</section>

			<div className="order-support-grid">
				<section className="order-support-panel">
					<h2>Support Notes</h2>
					<form className="order-support-form" onSubmit={addNote}>
						<select
							value={noteStatus}
							onChange={(event) => setNoteStatus(event.target.value)}
						>
							<option value="needs_review">Needs review</option>
							<option value="customer_contacted">Customer contacted</option>
							<option value="restaurant_contacted">Restaurant contacted</option>
							<option value="resolved">Resolved</option>
						</select>
						<textarea
							value={note}
							onChange={(event) => setNote(event.target.value)}
							placeholder="Internal support note"
						/>
						<button type="submit" disabled={saving}>
							Save Note
						</button>
					</form>
					<div className="order-support-list">
						{detail?.notes?.map((row) => (
							<div key={row.id}>
								<strong>{row.status || "note"}</strong>
								<span>{formatDate(row.createdAt)}</span>
								<p>{row.note}</p>
							</div>
						))}
						{!detail?.notes?.length && <p>No support notes yet.</p>}
					</div>
				</section>

				<section className="order-support-panel">
					<h2>Refunds</h2>
					{refundStorageError && <p role="alert">{refundStorageError}</p>}
					{order.raw?.externalPaymentReviewRequired && <p role="alert">Refunds blocked: an external payment change requires support reconciliation.</p>}
					{order.raw?.refundEffectsPending && <p role="status">An earlier refund is awaiting accounting reconciliation.</p>}
					{pendingRefund && <button type="button" disabled={saving} onClick={checkRefundStatus}>Check refund status</button>}
					<form className="order-support-form" onSubmit={submitRefund}>
						<label htmlFor="refund-payment">Original payment</label>
						<select id="refund-payment" value={selectedPayment?.paymentId || ""}
							disabled={saving || !!pendingRefund || refundPayments.length <= 1}
							onChange={event => { setSelectedPaymentId(event.target.value); setRefundAmount(""); }}>
							{refundPayments.length !== 1 && <option value="">Select payment</option>}
							{refundPayments.map((row, index) => <option key={row.paymentId || `order-${index}`} value={row.paymentId || ""}>
								{row.paymentIntentId || row.tenderType || "Payment"} · {formatMoney(row.capturedCents)} · {formatMoney(row.refundableCents)} remaining
							</option>)}
						</select>
						{selectedPayment?.unavailableReason && <p role="status">Refund unavailable: {selectedPayment.unavailableReason.replaceAll("_", " ")}</p>}
						{!refundPayments.length && <p role="status">Original payment details unavailable.</p>}
						<input
							type="number"
							step="0.01"
							min="0"
							max={(refundableCents / 100).toFixed(2)}
							value={refundAmount}
							onChange={(event) => setRefundAmount(event.target.value)}
							placeholder={`Max ${(refundableCents / 100).toFixed(2)}`}
							aria-label="Refund amount"
							disabled={!canRefundPayment || saving || !!pendingRefund}
						/>
						<textarea
							value={refundReason}
							onChange={(event) => setRefundReason(event.target.value)}
							placeholder="Refund reason"
							aria-label="Refund reason"
							disabled={!canRefundPayment || saving || !!pendingRefund}
						/>
						<button
							type="submit"
							disabled={saving || !!pendingRefund || !canRefundPayment}
						>
							Submit Refund
						</button>
					</form>
					<div className="order-support-list">
						{detail?.refunds?.map((row) => (
							<div key={row.id}>
								<strong>
									{formatMoney(row.amountCents)} · {row.status}
								</strong>
								<span>{row.stripeRefundId || row.id}</span>
								<p>{row.reason}</p>
								{row.status === "succeeded" && <p>{row.effectsComplete ? "Accounting complete" : "Accounting reconciliation pending"}</p>}
								{detail?.refundReceipts?.filter(ledger => ledger.refundId === row.id).map(ledger => (
									<button type="button" key={ledger.id} disabled={saving} onClick={() => printRefundReceipt(ledger)}>Print refund receipt</button>
								))}
							</div>
						))}
						{!detail?.refunds?.length && <p>No refunds recorded.</p>}
					</div>
					{detail?.paymentExceptions?.length > 0 && <div className="order-support-list">
						<h3>Provider events</h3>
						{detail.paymentExceptions.map(row => <div key={row.id}>
							<strong>{row.eventType}</strong>
							<p>{row.disputeStatus || row.refundStatus || row.status}</p>
							<span>{row.disputeId || row.refundId || row.chargeId}</span>
							{row.evidenceDueBy && <p>Evidence due: {formatDate(row.evidenceDueBy * 1000)}</p>}
							<p>Received: {formatDate(row.createdAt)}</p>
						</div>)}
					</div>}
				</section>
			</div>
		</div>
	);
};

export default OrderSupportDetail;
